import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { linksUrl } from '../../constants/enum';
import { ErrorCard, LoadingCard, MainContainer } from '../../components';
import { Button } from '@nextui-org/react';
import { faArrowLeft, faFileExcel, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { color } from 'framer-motion';
import { useSelector } from 'react-redux';
import("jspdf-autotable")
//import { autoTable } from "jspdf-autotable"

const GstLedger = () => {
  const {compName , gstNo } = useSelector((state) => state.company);
  
  const location = useLocation();
  const navigate = useNavigate();
  const [reportData, setReportData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totals, setTotals] = useState({
    credit: { taxable: 0, cgst: 0, sgst: 0, igst: 0, net: 0 },
    debit: { taxable: 0, cgst: 0, sgst: 0, igst: 0, net: 0 },
    balance: 0
  });

  useEffect(() => {
    if (location.state?.reportData) {
      try {
        const apiData = location.state.reportData;

        if (Array.isArray(apiData) && apiData.length > 0) {
          const ledgerData = apiData[0];
          const dataArray = Array.isArray(ledgerData) ? ledgerData : [ledgerData];

          // Calculate totals
          const creditTotals = { taxable: 0, cgst: 0, sgst: 0, igst: 0, net: 0 };
          const debitTotals = { taxable: 0, cgst: 0, sgst: 0, igst: 0, net: 0 };

          dataArray.forEach(item => {
            creditTotals.taxable += parseFloat(item.TaxableValueC || 0);
            creditTotals.cgst += parseFloat(item.CGSTAmtC || 0);
            creditTotals.sgst += parseFloat(item.SGSTAmtC || 0);
            creditTotals.igst += parseFloat(item.IGSTAmtC || 0);
            creditTotals.net += parseFloat(item.NetAmountC || 0);

            debitTotals.taxable += parseFloat(item.TaxableValueD || 0);
            debitTotals.cgst += parseFloat(item.CGSTAmtD || 0);
            debitTotals.sgst += parseFloat(item.SGSTAmtD || 0);
            debitTotals.igst += parseFloat(item.IGSTAmtD || 0);
            debitTotals.net += parseFloat(item.NetAmountD || 0);
          });

          const credittotal = (creditTotals.cgst + creditTotals.sgst + creditTotals.igst)
          const debittotal = (debitTotals.cgst + debitTotals.sgst + debitTotals.igst)

          const balance = (creditTotals.cgst + creditTotals.sgst + creditTotals.igst) -
            (debitTotals.cgst + debitTotals.sgst + debitTotals.igst);

          setTotals({
            credit: creditTotals,
            debit: debitTotals,
            balance,
            credittotal,
            debittotal
          });

          setReportData(dataArray);
        } else {
          setError("No valid report data available.");
        }
      } catch (err) {
        setError("Failed to process report data: " + err.message);
      }
      setIsLoading(false);
    } else {
      setError("No report data available. Please generate a report first.");
      setIsLoading(false);
    }
  }, [location.state]);

  const handleGoBack = () => {
    navigate(linksUrl.gstReport.mainGst);
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? dateString :
        date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '/');
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value || 0);
    return isNaN(num) ? '0.00' : num.toFixed(2);
  };

  const exportToExcel = () => {
    const wsData = [
      [
        'Date', 'Doc. Type', 'Bill No', 'Voucher No', 'Party Name', 'GST No', 'State',
        'Taxable Value (C)', 'CGST Amt (C)', 'SGST Amt (C)', 'IGST Amt (C)', 'Net Amount (C)',
        'Taxable Value (D)', 'CGST Amt (D)', 'SGST Amt (D)', 'IGST Amt (D)', 'Net Amount (D)'
      ],
      ...reportData.map(item => [
        formatDate(item.Bill_Date),
        item.Bill_Type_Name || '',
        item.Bill_No || '',
        item.VOU_NO || '',
        item.PartyName || '',
        item.GST_No || '',
        item.State || '',
        formatCurrency(item.TaxableValueC),
        formatCurrency(item.CGSTAmtC),
        formatCurrency(item.SGSTAmtC),
        formatCurrency(item.IGSTAmtC),
        formatCurrency(item.NetAmountC),
        formatCurrency(item.TaxableValueD),
        formatCurrency(item.CGSTAmtD),
        formatCurrency(item.SGSTAmtD),
        formatCurrency(item.IGSTAmtD),
        formatCurrency(item.NetAmountD)
      ]),
      [
        '', '', '', '', '', '', 'Total',
        formatCurrency(totals.credit.taxable),
        formatCurrency(totals.credit.cgst),
        formatCurrency(totals.credit.sgst),
        formatCurrency(totals.credit.igst),
        formatCurrency(totals.credit.net),
        formatCurrency(totals.debit.taxable),
        formatCurrency(totals.debit.cgst),
        formatCurrency(totals.debit.sgst),
        formatCurrency(totals.debit.igst),
        formatCurrency(totals.debit.net)
      ],
      [
        '', '', '', '', '', '', 'Balance',
        '', '', '', '', '',
        '',
        formatCurrency(totals.balance),
        '', '', ''
      ]
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "GST Ledger");
    XLSX.writeFile(wb, "GST_Ledger_Report.xlsx");
  };

const exportToPDF = () => {
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm'
  });

  // Set font to prevent line breaks between words
  doc.setFont('helvetica', 'normal');

  // Company information
  const companyName = compName;
  const GstNo = gstNo;
  const reportType = 'Report : GST Ledger';
  const fromDate = formatDate(location.state?.fromDate);
  const toDate = formatDate(location.state?.toDate);

  // Prepare data for the table
  const headers = [
    [
      'Date', 'Doc Type', 'Bill No', 'Vouch. No', 'Party Name', 'GST No', 'State',
      { 
        content: 'CREDIT', 
        colSpan: 5, 
        styles: { 
          fillColor: [230, 247, 230], 
          textColor: [0, 100, 0],
          fontStyle: 'bold'
        } 
      },
     
      { 
        content: 'DEBIT', 
        colSpan: 5, 
        styles: { 
          fillColor: [255, 230, 230], 
          textColor: [139, 0, 0],
          fontStyle: 'bold'
        } 
      },
    ],
    [
      '', '', '', '', '', '', '',
      'Taxable', 'CGST', 'SGST', 'IGST', 'Net Amt',
      'Taxable', 'CGST', 'SGST', 'IGST', 'Net Amt'
    ]
  ];

  // Shorten headers for better fit
  const shortenedBody = reportData.map(item => [
    formatDate(item.Bill_Date),
    item.Bill_Type_Name?.substring(0, 8) || '',
    item.Bill_No?.substring(0, 10) || '',
    item.VOU_NO?.substring(0, 8) || '',
    item.PartyName?.substring(0, 20) || '', // Limit party name length
    item.GST_No || '',
    item.State   || '',
    formatCurrency(item.TaxableValueC),
    formatCurrency(item.CGSTAmtC),
    formatCurrency(item.SGSTAmtC),
    formatCurrency(item.IGSTAmtC),
    formatCurrency(item.NetAmountC),
    formatCurrency(item.TaxableValueD),
    formatCurrency(item.CGSTAmtD),
    formatCurrency(item.SGSTAmtD),
    formatCurrency(item.IGSTAmtD),
    formatCurrency(item.NetAmountD)
  ]);

  // Add totals row with colspan
shortenedBody.push([
  { 
    content: 'Total',
    colSpan: 7,
    styles: { fontStyle: 'bold',halign : 'left' }
  },
  { 
    content: formatCurrency(totals.credit.taxable),
    styles: { fontStyle: 'bold' }
  },
  { 
    content: formatCurrency(totals.credit.cgst),
    styles: { fontStyle: 'bold' }
  },
  { 
    content: formatCurrency(totals.credit.sgst),
    styles: { fontStyle: 'bold' }
  },
  { 
    content: formatCurrency(totals.credit.igst),
    styles: { fontStyle: 'bold' }
  },
  { 
    content: formatCurrency(totals.credit.net),
    styles: { fontStyle: 'bold' }
  },
  { 
    content: formatCurrency(totals.debit.taxable),
    styles: { fontStyle: 'bold' }
  },
  { 
    content: formatCurrency(totals.debit.cgst),
    styles: { fontStyle: 'bold' }
  },
  { 
    content: formatCurrency(totals.debit.sgst),
    styles: { fontStyle: 'bold' }
  },
  { 
    content: formatCurrency(totals.debit.igst),
    styles: { fontStyle: 'bold' }
  },
  { 
    content: formatCurrency(totals.debit.net),
    styles: { fontStyle: 'bold' }
  }
]);

//GST totals
shortenedBody.push([
  { 
    content: 'GST Total',
    colSpan: 7,
    styles: { fontStyle: 'bold', halign : 'left'}
  },
  '', // This empty string is needed to maintain column count
  { 
    content: formatCurrency(totals.credittotal),
    colSpan:3,
    styles: { fontStyle: 'bold' }
  },
  '', '', { 
    content: formatCurrency(totals.debittotal),
    colSpan:3,
    styles: { fontStyle: 'bold' }
  }
]);

// Add balance row with colspan
shortenedBody.push([
  { 
    content: 'Balance (CREDIT   - DEBIT)',
    colSpan: 7,
    styles: { fontStyle: 'bold' , halign : 'left'}
  },
  '', // This empty string is needed to maintain column count
  { 
    content: formatCurrency(totals.balance),
    colSpan:8,
    styles: { fontStyle: 'bold' , textColor :'blue' , fillColor : 'white'}
  },
  '', '', '', '', '', '', '', ''
]);

  // Column styles with optimized widths (in mm)
const columnStyles = {
    0: { cellWidth: 15, halign: 'center' },  // Date
    1: { cellWidth: 12, halign: 'center' },  // Doc Type
    2: { cellWidth: 15, halign: 'center' },  // Bill No
    3: { cellWidth: 15, halign: 'center' },  // Voucher No
    4: { cellWidth: 30, halign: 'center' },  // Party Name
    5: { cellWidth: 23, halign: 'center' },  // GST No
    6: { cellWidth: 30, halign: 'center' },  // State
    // Credit columns (green background)
    7: { 
        cellWidth: 15, 
        halign: 'center',
        fillColor: [230, 247, 230], // Light green
        textColor: [0, 100, 0]      // Dark green text
    },
    8: { 
        cellWidth: 15, 
        halign: 'center',
        fillColor: [230, 247, 230],
        textColor: [0, 100, 0]
    },
    9: { 
        cellWidth: 15, 
        halign: 'center',
        fillColor: [230, 247, 230],
        textColor: [0, 100, 0]
    },
    10: { 
        cellWidth: 15, 
        halign: 'center',
        fillColor: [230, 247, 230],
        textColor: [0, 100, 0]
    },
    11: { 
        cellWidth: 15, 
        halign: 'center',
        fillColor: [230, 247, 230],
        textColor: [0, 100, 0]
    },
    // Debit columns (red background)
    12: { 
        cellWidth: 15, 
        halign: 'center',
        fillColor: [255, 230, 230], // Light red
        textColor: [139, 0, 0]     // Dark red text
    },
    13: { 
        cellWidth: 15, 
        halign: 'center',
        fillColor: [255, 230, 230],
        textColor: [139, 0, 0]
    },
    14: { 
        cellWidth: 15, 
        halign: 'center',
        fillColor: [255, 230, 230],
        textColor: [139, 0, 0]
    },
    15: { 
        cellWidth: 15, 
        halign: 'center',
        fillColor: [255, 230, 230],
        textColor: [139, 0, 0]
    },
    16: { 
        cellWidth: 15, 
        halign: 'center',
        fillColor: [255, 230, 230],
        textColor: [139, 0, 0]
    }
};

  // Calculate total table width
  const totalWidth = Object.values(columnStyles).reduce((sum, style) => sum + style.cellWidth, 0);

  
doc.autoTable({
  startY: 30,  // Push table down to leave room for header (works for all pages)
  head: headers,
  body: shortenedBody,
  theme: 'grid',
  tableWidth: 'wrap',
  margin: { left: 3, right: 3, top: 30 }, 
  headStyles: {
    fillColor: [41, 128, 185],
    textColor: 255,
    fontStyle: 'bold',
    halign: 'center',
    fontSize: 7
  },
  columnStyles: columnStyles,
  styles: {
    fontSize: 6,
    cellPadding: 1.5,
    overflow: 'linebreak',
    valign: 'middle',
    font: 'helvetica',
    lineWidth: 0.1,
    lineColor: [200, 200, 200]
  },
  bodyStyles: {
    textColor: [0, 0, 0],
    fontStyle: 'normal'
  },
  alternateRowStyles: {
    fillColor: [245, 245, 245]
  },
  didDrawPage: function (data) {
    // Header
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(companyName, 5, 10);
    doc.text(`GST No: ${GstNo}`, 5, 15);
    doc.text(reportType, 5, 20);
    doc.text(`From: ${fromDate} To: ${toDate}`, doc.internal.pageSize.width - 5, 20, { align: 'right' });

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(
      `Page ${data.pageNumber}`,
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 5,
      { align: 'center' }
    );
  },
  willDrawCell: function (data) {
    if ([0, 1, 2, 3, 5, 6].includes(data.column.index)) {
      data.cell.text = data.cell.text.join(' ');
    }
    if ([7,8,9,10,11,12,13,14,15,16].includes(data.column.index)) {
      data.cell.text = [data.cell.text[0]];
    }
  },
  didParseCell: function (data) {
    if (data.column.index === 4 && data.cell.text[0].length > 20) {
      data.cell.text = [data.cell.text[0].substring(0, 17) + '...'];
    }
  }
});


  doc.save('GST_Ledger_Report.pdf');
};

  if (isLoading) {
    return <LoadingCard />;
  }

  if (error) {
    return (
      <MainContainer
        pageName="GST Ledger Report"
        breadcrumbs={["Reports", "GST Reports", "GST Ledger"]}
      >
        <div className="flex flex-col items-center justify-center py-8">
          <p className="text-red-500 mb-4">{error}</p>
          <Button
            color="primary"
            startContent={<FontAwesomeIcon icon={faArrowLeft} />}
            onClick={handleGoBack}
          >
            Back to GST Reports
          </Button>
        </div>
      </MainContainer>
    );
  }

return (
    <MainContainer
      pageName="GST Ledger Report"
      breadcrumbs={["Reports", "GST Reports", "GST Ledger"]}
    >
      <div className="mb-4 flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">GST Ledger Details</h2>
          <p className="text-sm text-gray-600">
            Showing results from {formatDate(location.state?.fromDate)} to {formatDate(location.state?.toDate)}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            color="success"
            startContent={<FontAwesomeIcon icon={faFileExcel} />}
            onPress={exportToExcel}
            size="sm"
          >
            Export Excel
          </Button>
          <Button
            color="danger"
            startContent={<FontAwesomeIcon icon={faFilePdf} />}
            onPress={exportToPDF}
            size="sm"
          >
            Export PDF
          </Button>
          <Button
            color="primary"
            variant="bordered"
            startContent={<FontAwesomeIcon icon={faArrowLeft} />}
            onPress={handleGoBack}
            size="sm"
          >
            Back
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto shadow-md rounded-lg">
        <table className="min-w-full border-collapse">
          <thead>
            <tr>
              <th 
                className="px-3 py-2 bg-gray-100 text-center font-medium text-gray-700 border border-gray-300" 
                colSpan="7"
              >
                DETAILS
              </th>
              <th 
                className="px-3 py-2 bg-green-50 text-green-800 text-center font-medium border border-gray-300" 
                colSpan="5"
              >
                CREDIT
              </th>
              <th 
                className="px-3 py-2 bg-red-50 text-red-800 text-center font-medium border border-gray-300" 
                colSpan="5"
              >
                DEBIT
              </th>
            </tr>
            <tr className="bg-gray-50">
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300">Date</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300">Doc. Type</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300">Bill No</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300">Voucher No</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300">Party Name</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300">GST No</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-300">State</th>

              {/* Credit headers */}
              <th className="px-3 py-2 text-right text-xs font-medium text-green-700 uppercase tracking-wider border border-gray-300">Taxable Value</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-green-700 uppercase tracking-wider border border-gray-300">CGST Amt</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-green-700 uppercase tracking-wider border border-gray-300">SGST Amt</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-green-700 uppercase tracking-wider border border-gray-300">IGST Amt</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-green-700 uppercase tracking-wider border border-gray-300">Net Amount</th>

              {/* Debit headers */}
              <th className="px-3 py-2 text-right text-xs font-medium text-red-700 uppercase tracking-wider border border-gray-300">Taxable Value</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-red-700 uppercase tracking-wider border border-gray-300">CGST Amt</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-red-700 uppercase tracking-wider border border-gray-300">SGST Amt</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-red-700 uppercase tracking-wider border border-gray-300">IGST Amt</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-red-700 uppercase tracking-wider border border-gray-300">Net Amount</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {reportData.map((item, index) => (
              <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 border border-gray-300">{formatDate(item.Bill_Date)}</td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 border border-gray-300">{item.Bill_Type_Name || ''}</td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 border border-gray-300">{item.Bill_No || ''}</td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 border border-gray-300">{item.VOU_NO || ''}</td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 border border-gray-300">{item.PartyName || ''}</td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 border border-gray-300">{item.GST_No || ''}</td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 border border-gray-300">{item.State || ''}</td>

                {/* Credit values */}
                <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-green-700 border border-gray-300">{formatCurrency(item.TaxableValueC)}</td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-green-700 border border-gray-300">{formatCurrency(item.CGSTAmtC)}</td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-green-700 border border-gray-300">{formatCurrency(item.SGSTAmtC)}</td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-green-700 border border-gray-300">{formatCurrency(item.IGSTAmtC)}</td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-green-700 font-medium border border-gray-300">{formatCurrency(item.NetAmountC)}</td>

                {/* Debit values */}
                <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-red-700 border border-gray-300">{formatCurrency(item.TaxableValueD)}</td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-red-700 border border-gray-300">{formatCurrency(item.CGSTAmtD)}</td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-red-700 border border-gray-300">{formatCurrency(item.SGSTAmtD)}</td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-red-700 border border-gray-300">{formatCurrency(item.IGSTAmtD)}</td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-red-700 font-medium border border-gray-300">{formatCurrency(item.NetAmountD)}</td>
              </tr>
            ))}

            {/* Totals row */}
            <tr className="bg-gray-100 font-semibold">
              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 border border-gray-300" colSpan="7">Total</td>

              {/* Credit totals */}
              <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-green-700 border border-gray-300">{formatCurrency(totals.credit.taxable)}</td>
              <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-green-700 border border-gray-300">{formatCurrency(totals.credit.cgst)}</td>
              <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-green-700 border border-gray-300">{formatCurrency(totals.credit.sgst)}</td>
              <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-green-700 border border-gray-300">{formatCurrency(totals.credit.igst)}</td>
              <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-green-700 font-medium border border-gray-300">{formatCurrency(totals.credit.net)}</td>

              {/* Debit totals */}
              <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-red-700 border border-gray-300">{formatCurrency(totals.debit.taxable)}</td>
              <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-red-700 border border-gray-300">{formatCurrency(totals.debit.cgst)}</td>
              <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-red-700 border border-gray-300">{formatCurrency(totals.debit.sgst)}</td>
              <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-red-700 border border-gray-300">{formatCurrency(totals.debit.igst)}</td>
              <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-red-700 font-medium border border-gray-300">{formatCurrency(totals.debit.net)}</td>
            </tr>

            {/* Credit/Debit Summary */}
            <tr className="bg-blue-50 font-bold">
              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 border border-gray-300" colSpan="7">GST Total</td>
              <td className="px-3 py-2 whitespace-nowrap text-sm text-right border border-gray-300"></td>
              <td className="px-3 py-2 whitespace-nowrap text-sm text-center text-green-800 border border-gray-300" colSpan="3">
                CREDIT TOTAL: {formatCurrency(totals.credit.cgst + totals.credit.sgst + totals.credit.igst)}
              </td>
              <td className="px-3 py-2 whitespace-nowrap text-sm text-right border border-gray-300"></td>
              <td className="px-3 py-2 whitespace-nowrap text-sm text-right border border-gray-300"></td>
              <td className="px-3 py-2 whitespace-nowrap text-sm text-center text-red-800 border border-gray-300" colSpan="3">
                DEBIT TOTAL: {formatCurrency(totals.debit.cgst + totals.debit.sgst + totals.debit.igst)}
              </td>
              <td className="px-3 py-2 whitespace-nowrap text-sm text-right border border-gray-300"></td>
            </tr>

            {/* Balance row */}
            <tr className="bg-indigo-50 font-bold">
              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 border border-gray-300" colSpan="7">NET BALANCE (Credit - Debit)</td>
              <td className="px-3 py-2 whitespace-nowrap text-sm text-center text-indigo-800 border border-gray-300" colSpan="10">
                {formatCurrency(totals.balance)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </MainContainer>
  );
};

export default GstLedger;